// Tool-window management. Tool windows are the graph / data-grid / diagnostics
// WebviewWindows (the main application window is left alone). Ports of the C++
// `Ui_*_all_tool_windows` helpers used by the Tool Sets dialog buttons and the
// Window menu: count (Ui_get_num_tool_windows), close all
// (Ui_delete_all_tool_windows), and bring all to front
// (Ui_bring_to_front_all_tool_windows).
//
// Desktop (Tauri) enumerates the windows directly. On the webserver, tool
// windows are separate browser tabs opened via window.open, which Tauri can't
// see, so they coordinate over a same-origin BroadcastChannel:
//   * each tool tab ANNOUNCES `open` when it registers and `closed` on pagehide;
//     the main window keeps a live registry of open tabs — so the Window menu's
//     enable state is correct immediately, with no per-open ping race (the race
//     intermittently greyed Close All when a backgrounded tab's reply missed the
//     collect window);
//   * the main window broadcasts `close-all` / `show-all`; each tab closes or
//     focuses ITSELF (window.open popups are script-closable and self-focusable).
// Every message carries the opener's `?live=<config>` scope and tabs only act on
// a scope match, so a turbine session only ever counts / closes / raises its OWN
// tool windows — never another session's (or another user's, since
// BroadcastChannel is per-browser).

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

// Tool windows are spawned as separate WebviewWindows with these label prefixes
// (see openGraphWindow / openDataGridWindow / openDiagnosticsWindow). The main
// window's label is "main" and never matches, so it is always left open.
const TOOL_WINDOW_PREFIXES = ["graph-", "datagrid-", "diagnostics-"] as const;

export function isToolWindowLabel(label: string): boolean {
  return TOOL_WINDOW_PREFIXES.some((prefix) => label.startsWith(prefix));
}

// --- Web (browser-tab) coordination ----------------------------------------

// Same-origin channel shared by the main window and every tool-window tab.
const TOOL_WINDOW_CHANNEL = "tiger-tool-windows";
// Settle window for an on-demand count: how long to let tabs answer the
// reconcile ping before reading the registry the first time. The live
// open/closed announcements keep it accurate afterwards, so this only matters
// for the very first read.
const RECONCILE_MS = 120;
// Each web tool tab sets window.name to this prefix + its registry id, so the
// main window can raise it by re-targeting that name (see showAllToolWindows).
const TOOL_WINDOW_NAME_PREFIX = "tigertool:";

type ToolWindowMessage =
  | { type: "open"; scope: string; id: string }
  | { type: "closed"; scope: string; id: string }
  | { type: "ping" }
  | { type: "close-all"; scope: string };

function hasBroadcastChannel(): boolean {
  return typeof BroadcastChannel !== "undefined";
}

/** This window's tool-window scope: the `?live=<config>` token, or "" when the
 *  window isn't a live/replay tab. Tool windows opened from a `?live=` tab carry
 *  the same token (see appendLiveParam), so messages scoped by it naturally stay
 *  within one turbine session. Exported so the tool-set snapshot collector
 *  (toolSetSnapshot.ts) scopes its request the same way. */
export function liveScope(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("live") ?? "";
}

// ---- Main-window live registry of open tool windows (web) ------------------

const registry = new Map<string, string>(); // tool-window id -> its `?live=` scope
let registryChannel: BroadcastChannel | null = null;
let registryStarted = false;
const changeListeners = new Set<() => void>();

function notifyRegistryChange(): void {
  changeListeners.forEach((l) => {
    try {
      l();
    } catch (e) {
      console.error("tool-window registry listener failed:", e);
    }
  });
}

/** Start the main window's live registry: listen for tool-window open/closed
 *  announcements and ask any already-open tabs to re-announce. Idempotent;
 *  no-op on desktop / where BroadcastChannel is unavailable. */
function startRegistry(): void {
  if (registryStarted || isTauri() || !hasBroadcastChannel() || typeof window === "undefined") {
    return;
  }
  registryStarted = true;
  registryChannel = new BroadcastChannel(TOOL_WINDOW_CHANNEL);
  registryChannel.onmessage = (ev: MessageEvent) => {
    const m = ev.data as ToolWindowMessage | null;
    if (!m) return;
    if (m.type === "open") {
      const had = registry.get(m.id);
      registry.set(m.id, m.scope);
      if (had === undefined) notifyRegistryChange();
    } else if (m.type === "closed") {
      if (registry.delete(m.id)) notifyRegistryChange();
    }
  };
  // Reconcile: ask tool windows that opened before we started to re-announce.
  registryChannel.postMessage({ type: "ping" } satisfies ToolWindowMessage);
}

/** Subscribe to tool-window registry changes (web main window); the listener
 *  fires whenever an in- or out-of-scope tab opens or closes. Starts the
 *  registry if needed. Returns an unsubscribe fn. No-op on desktop, where there
 *  is no registry (Tauri enumerates windows on demand). */
export function subscribeToolWindows(listener: () => void): () => void {
  if (isTauri() || !hasBroadcastChannel()) return () => {};
  startRegistry();
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/** Synchronous count of open tool windows in THIS window's `?live=` scope, read
 *  from the live registry. 0 on desktop / without BroadcastChannel. Pair with
 *  subscribeToolWindows for a race-free Window-menu enable state. */
export function getOpenToolWindowCount(): number {
  if (isTauri() || !hasBroadcastChannel()) return 0;
  const scope = liveScope();
  let n = 0;
  registry.forEach((s) => {
    if (s === scope) n++;
  });
  return n;
}

/** Ids of the registered tool windows in the given `?live=` scope (web). Used by
 *  showAllToolWindows to re-target each window by its window.name. */
function openToolWindowIdsForScope(scope: string): string[] {
  const ids: string[] = [];
  registry.forEach((s, id) => {
    if (s === scope) ids.push(id);
  });
  return ids;
}

/** Test-only: drop all registry state so each test starts clean. */
export function resetToolWindowRegistryForTest(): void {
  registry.clear();
  changeListeners.clear();
  if (registryChannel) registryChannel.close();
  registryChannel = null;
  registryStarted = false;
}

/** Broadcast a one-shot action to the tool windows. Reuses the long-lived
 *  registry channel when the main window has one; otherwise opens a throwaway
 *  channel and closes it after the message has had time to flush. */
function postToolWindowMessage(msg: ToolWindowMessage): void {
  if (!hasBroadcastChannel()) return;
  if (registryChannel) {
    registryChannel.postMessage(msg);
    return;
  }
  const ch = new BroadcastChannel(TOOL_WINDOW_CHANNEL);
  ch.postMessage(msg);
  setTimeout(() => ch.close(), RECONCILE_MS);
}

export interface RegisterToolWindowOptions {
  /** Override the `?live=<config>` scope. Defaults to this document's `?live=`.
   *  Used by tests to simulate several tabs (different scopes) in one realm. */
  scope?: string;
  /** Override the generated id (and so the window.name). Tests use this to make
   *  the re-target name deterministic. */
  id?: string;
  /** Override the close action. Defaults to closing this browser tab. */
  onClose?: () => void;
}

/** Register a web tool-window tab so the main window's Window menu can count,
 *  close, and raise it. Announces `open` now and `closed` on pagehide, re-announces
 *  on a reconcile `ping`, and runs `onClose` on a scope-matching `close-all`. Sets
 *  window.name to a stable token so the main window can raise it (showAllToolWindows
 *  re-targets that name from the activated Show All click — a tab can't raise itself
 *  from a background message). Only acts on its own `?live=` scope, so a `?live=A`
 *  session never touches a `?live=B` tab. No-op on desktop (Tauri tracks windows
 *  directly) and where BroadcastChannel is unavailable. Returns a cleanup fn. */
export function registerWebToolWindow(opts?: RegisterToolWindowOptions): () => void {
  if (typeof window === "undefined" || isTauri() || !hasBroadcastChannel()) {
    return () => {};
  }
  const scope = opts?.scope ?? liveScope();
  const onClose =
    opts?.onClose ??
    (() => {
      try {
        window.close();
      } catch {
        // Some browsers refuse window.close() for non-script-opened tabs; ignore.
      }
    });
  const id = opts?.id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  // Name the window so the main window can raise it by re-targeting the name.
  try {
    window.name = TOOL_WINDOW_NAME_PREFIX + id;
  } catch {
    // window.name is normally writable; ignore if a sandbox refuses.
  }
  const ch = new BroadcastChannel(TOOL_WINDOW_CHANNEL);
  const announce = (type: "open" | "closed") =>
    ch.postMessage({ type, scope, id } satisfies ToolWindowMessage);

  ch.onmessage = (ev: MessageEvent) => {
    const m = ev.data as ToolWindowMessage | null;
    if (!m) return;
    if (m.type === "ping") {
      announce("open"); // reconcile: re-announce regardless of scope
      return;
    }
    if (m.scope !== scope) return; // scoped actions only
    if (m.type === "close-all") onClose();
  };

  const onPageHide = () => announce("closed");
  window.addEventListener("pagehide", onPageHide);
  announce("open");

  return () => {
    window.removeEventListener("pagehide", onPageHide);
    ch.onmessage = null; // stop answering reconcile pings immediately
    announce("closed");
    // Defer the close so the "closed" message flushes to the registry first.
    setTimeout(() => ch.close(), RECONCILE_MS);
  };
}

/** Count the open graph / data-grid / diagnostics tool windows. Port of C++
 *  `Ui_get_num_tool_windows`, used to enable/disable "Close All" / "Show All".
 *  On desktop this enumerates the Tauri windows; on the webserver it reads the
 *  live registry (scoped to this session's `?live=`), settling once for the
 *  reconcile ping so a first call right after start still sees pre-existing
 *  tabs. */
export async function countToolWindows(): Promise<number> {
  if (!isTauri()) {
    if (!hasBroadcastChannel()) return 0;
    const fresh = !registryStarted;
    startRegistry();
    if (fresh) await new Promise((r) => setTimeout(r, RECONCILE_MS));
    return getOpenToolWindowCount();
  }

  const { getAllWebviewWindows } = await import("@tauri-apps/api/webviewWindow");
  const windows = await getAllWebviewWindows();

  return windows.filter((w) => isToolWindowLabel(w.label)).length;
}

/** Bring every graph / data-grid / diagnostics tool window to the front,
 *  un-minimising any that are minimised. Port of C++
 *  `Ui_bring_to_front_all_tool_windows`. On desktop, restore + show + focus each
 *  window. On the webserver, re-target each in-scope tool window by name with
 *  window.open("", name): the tool windows are window.open popups (separate OS
 *  windows the browser lets a script raise), and re-opening an existing named
 *  window restores + raises it without reloading. This must run from the Show All
 *  click (it carries user activation) and synchronously — a tab can't raise itself
 *  from a background message, which is why a focus broadcast did nothing. */
export async function showAllToolWindows(): Promise<void> {
  if (!isTauri()) {
    if (typeof window === "undefined" || !hasBroadcastChannel()) return;
    startRegistry();
    for (const id of openToolWindowIdsForScope(liveScope())) {
      try {
        window.open("", TOOL_WINDOW_NAME_PREFIX + id);
      } catch (e) {
        console.error(`show tool window ${id} failed:`, e);
      }
    }
    return;
  }

  const { getAllWebviewWindows } = await import("@tauri-apps/api/webviewWindow");
  const windows = await getAllWebviewWindows();

  // Sequential so the windows raise in list order and focus lands predictably.
  for (const w of windows.filter((win) => isToolWindowLabel(win.label))) {
    try {
      // Unconditionally restore: don't gate on isMinimized(), which can report
      // stale state for windows fetched via getAllWebviewWindows() (so a
      // minimised window never gets un-minimised). unminimize() on an already
      // restored window is harmless. Equivalent to the SW_RESTORE in the C++
      // show_win. show() then setFocus() bring it visible and to the front.
      await w.unminimize();
      await w.show();
      await w.setFocus();
    } catch (e) {
      console.error(`show ${w.label} failed:`, e);
    }
  }
}

/** Close all open graph / data-grid / diagnostics tool windows, leaving the
 *  main window. On desktop this closes the Tauri windows; on the webserver it
 *  broadcasts close-all to the browser tabs in this session's `?live=` scope,
 *  each of which closes itself (registerWebToolWindow). */
export async function closeAllToolWindows(): Promise<void> {
  if (!isTauri()) {
    postToolWindowMessage({ type: "close-all", scope: liveScope() });
    return;
  }

  const { getAllWebviewWindows } = await import("@tauri-apps/api/webviewWindow");
  const windows = await getAllWebviewWindows();

  await Promise.all(
    windows
      .filter((w) => isToolWindowLabel(w.label))
      .map((w) =>
        w.close().catch((e) => console.error(`close ${w.label} failed:`, e)),
      ),
  );
}
